/**

 */
import { debounce } from 'lodash';

/**
 * @deprecated
 * Manage all API requests so that at most one is pending at any time.
 * If multiple requests are made while a request is pending,
 * only the most recent will be executed.
 *
 * Only use this if you need it for your event handling. Preferably, use the item authoring framework to handle
 * queuing requests for you instead.
 */
class RequestManager {
  declare private debounceRequest: <T>(promise: () => Promise<T>) => Promise<T> | false;

  private inProgress = false;

  private pendingRequest: (() => Promise<unknown>) | null = null;

  private deferredPromise: Promise<unknown> | null = null;

  private promiseResolvers: {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  } | null = null;

  constructor(delay = 500) {
    this.debounceRequest = debounce(this.run, delay);
  }

  run<T>(request: () => Promise<T>): Promise<T> | false {
    if (this.inProgress) {
      this.pendingRequest = request;
      return false;
    }

    let currentResolvers = this.promiseResolvers;
    const promise = new Promise<T>((resolve, reject) => {
      if (!currentResolvers) {
        currentResolvers = { resolve: resolve as (value: unknown) => void, reject };
      }
    });

    this.promiseResolvers = null;
    this.deferredPromise = null;
    this.inProgress = true;
    this.pendingRequest = null;

    request()
      .then((result: T) => currentResolvers?.resolve(result))
      .catch((error: unknown) => currentResolvers?.reject(error))
      .finally(() => {
        this.inProgress = false;

        if (this.pendingRequest) {
          this.run(this.pendingRequest);
        }
      });

    return promise;
  }

  debouncedRun<T>(request: () => Promise<T>): Promise<T> {
    if (!this.deferredPromise) {
      this.deferredPromise = new Promise<T>((resolve, reject) => {
        this.promiseResolvers = { resolve: resolve as (value: unknown) => void, reject };
      });
    }

    this.debounceRequest(request);

    return this.deferredPromise as Promise<T>;
  }
}

export default RequestManager;
